import { useState, useEffect, useCallback, useRef } from 'react';
import { contentService } from '../services/content';
import {
  AdminContentItemDto,
  AdminContentDetailDto,
  ContentStatus,
  ContentSummary,
  ContentListQueryParams,
} from '../types/content';
import { showToast } from '../utils/helpers';

// ============================================================================
// useContentList - Paginated content list with filters
// ============================================================================

interface UseContentListState {
  items: AdminContentItemDto[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface UseContentListReturn extends UseContentListState {
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
}

export function useContentList(params: ContentListQueryParams): UseContentListReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;
  const currentPage = Math.floor(offset / limit) + 1;

  const [state, setState] = useState<UseContentListState>({
    items: [],
    total: 0,
    loading: true,
    error: null,
    page: currentPage,
    pageSize: limit,
    totalPages: 0,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchContent = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await contentService.getContentList(paramsRef.current);
      const { items, pagination } = response.data;
      const totalPages = Math.ceil(pagination.total / pagination.limit);
      const page = Math.floor(pagination.offset / pagination.limit) + 1;

      setState({
        items,
        total: pagination.total,
        loading: false,
        error: null,
        page,
        pageSize: pagination.limit,
        totalPages,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch content';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [
    fetchContent,
    params.status,
    params.type,
    params.source,
    params.search,
    params.offset,
    params.limit,
    params.sortBy,
    params.sortOrder,
  ]);

  const setPage = useCallback(
    (newPage: number) => {
      const newOffset = (newPage - 1) * limit;
      paramsRef.current = { ...paramsRef.current, offset: newOffset };
      fetchContent();
    },
    [fetchContent, limit]
  );

  return {
    ...state,
    refetch: fetchContent,
    setPage,
  };
}

// ============================================================================
// useContentDetail - Single content item details
// ============================================================================

interface UseContentDetailState {
  content: AdminContentDetailDto | null;
  loading: boolean;
  error: string | null;
}

interface UseContentDetailReturn extends UseContentDetailState {
  refetch: () => Promise<void>;
}

export function useContentDetail(contentId: string | null): UseContentDetailReturn {
  const [state, setState] = useState<UseContentDetailState>({
    content: null,
    loading: false,
    error: null,
  });

  const fetchDetail = useCallback(async () => {
    if (!contentId) {
      setState({ content: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await contentService.getContentDetail(contentId);
      setState({ content: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch content details';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [contentId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    ...state,
    refetch: fetchDetail,
  };
}

// ============================================================================
// useContentSummary - Status counts for dashboard cards
// ============================================================================

interface UseContentSummaryState {
  summary: ContentSummary | null;
  loading: boolean;
  error: string | null;
}

interface UseContentSummaryReturn extends UseContentSummaryState {
  refetch: () => Promise<void>;
}

export function useContentSummary(): UseContentSummaryReturn {
  const [state, setState] = useState<UseContentSummaryState>({
    summary: null,
    loading: true,
    error: null,
  });

  const fetchSummary = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await contentService.getContentSummary();
      setState({ summary: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch content summary';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    ...state,
    refetch: fetchSummary,
  };
}

// ============================================================================
// useOwnerCounts - True per-owner item counts for a given status
// ============================================================================

export function useOwnerCounts(status: ContentStatus | 'ALL' | undefined) {
  const [counts, setCounts] = useState<Map<number, number>>(new Map());
  const statusRef = useRef(status);
  statusRef.current = status;

  const fetchCounts = useCallback(async () => {
    const s = statusRef.current;
    if (!s || s === 'ALL') {
      setCounts(new Map());
      return;
    }

    try {
      // Fetch up to 100 items (backend max) to build owner counts
      const response = await contentService.getContentList({
        status: s as ContentStatus,
        limit: 100,
        offset: 0,
      });
      const map = new Map<number, number>();
      for (const item of response.data.items) {
        map.set(item.owner.id, (map.get(item.owner.id) || 0) + 1);
      }
      // If there are more than 100 items, fetch additional pages
      const { total } = response.data.pagination;
      if (total > 100) {
        let offset = 100;
        while (offset < total) {
          const next = await contentService.getContentList({
            status: s as ContentStatus,
            limit: 100,
            offset,
          });
          for (const item of next.data.items) {
            map.set(item.owner.id, (map.get(item.owner.id) || 0) + 1);
          }
          offset += 100;
        }
      }
      setCounts(map);
    } catch {
      setCounts(new Map());
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, status]);

  return { ownerCounts: counts, refetchOwnerCounts: fetchCounts };
}

// ============================================================================
// useContentActions - Approve/reject/bulk mutations
// ============================================================================

export function useContentActions() {
  const [loading, setLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const approve = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await contentService.approveContent(id);
      showToast('success', 'Content approved');
      return true;
    } catch (err) {
      showToast('error', 'Failed to approve content');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const revert = useCallback(async (id: string, reason?: string) => {
    setLoading(true);
    try {
      await contentService.revertContent(id, reason);
      showToast('success', 'Content moved back to review');
      return true;
    } catch (err) {
      showToast('error', 'Failed to revert content');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const reject = useCallback(async (id: string, reason?: string) => {
    setLoading(true);
    try {
      await contentService.rejectContent(id, reason);
      showToast('success', 'Content rejected');
      return true;
    } catch (err) {
      showToast('error', 'Failed to reject content');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const bulkApprove = useCallback(async (ids: string[]) => {
    setLoading(true);
    try {
      const response = await contentService.bulkApproveContent(ids);
      const { approvedCount, skippedCount } = response.data;
      if (skippedCount && skippedCount > 0) {
        showToast('warning', `Approved ${approvedCount}, skipped ${skippedCount}`);
      } else {
        showToast('success', `${approvedCount} item(s) approved`);
      }
      return true;
    } catch (err) {
      showToast('error', 'Failed to bulk approve content');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const bulkReject = useCallback(async (ids: string[], reason?: string) => {
    setLoading(true);
    try {
      const response = await contentService.bulkRejectContent(ids, reason);
      const { rejectedCount, skippedCount } = response.data;
      if (skippedCount && skippedCount > 0) {
        showToast('warning', `Rejected ${rejectedCount}, skipped ${skippedCount}`);
      } else {
        showToast('success', `${rejectedCount} item(s) rejected`);
      }
      return true;
    } catch (err) {
      showToast('error', 'Failed to bulk reject content');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const regenerate = useCallback(async (id: string, prompt?: string) => {
    setRegenerateLoading(true);
    try {
      const res = await contentService.regenerateContent(id, prompt);
      showToast('success', res.message || 'Video regeneration queued');
      return true;
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to regenerate video';
      showToast('error', message);
      return false;
    } finally {
      setRegenerateLoading(false);
    }
  }, []);

  const uploadTikTok = useCallback(async (tiktokVideoId: string, file: File) => {
    setUploadLoading(true);
    try {
      await contentService.uploadTikTokVideo(tiktokVideoId, file);
      showToast('success', 'TikTok video uploaded successfully');
      return true;
    } catch (err) {
      showToast('error', 'Failed to upload TikTok video');
      return false;
    } finally {
      setUploadLoading(false);
    }
  }, []);

  const importTikTok = useCallback(async (tiktokVideoId: string) => {
    setImportLoading(true);
    try {
      await contentService.importTikTokVideo(tiktokVideoId);
      showToast('success', 'TikTok video imported successfully');
      return true;
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to auto-import TikTok video';
      showToast('error', message);
      return false;
    } finally {
      setImportLoading(false);
    }
  }, []);

  return {
    loading,
    regenerateLoading,
    uploadLoading,
    importLoading,
    approve,
    revert,
    reject,
    regenerate,
    bulkApprove,
    bulkReject,
    uploadTikTok,
    importTikTok,
  };
}
